import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import useStyles from '../liability-coverages-styles';

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Description is required'),
  amount: Yup.number().required('Estimated receipts is required').positive('Must be a positive number')
});

const FIELD_NAME = 'farmLiabilityCoverage.customFarmWorkItems';

const CustomFarming = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const items = getIn(values, FIELD_NAME) || [];

  return (
    <div className={classes.sectionContainer}>
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Custom farming</div>}
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((item, index) => {
            const detailFields = [];
            detailFields.push({
              name: 'description',
              label: 'Description',
              value: item.description || 'N/A',
              size: 12
            });
            detailFields.push({
              name: 'amount',
              label: 'Estimated receipts (per year)',
              value: item.amount != null ? `$${Number(item.amount).toLocaleString()}` : 'N/A',
              size: 6
            });
            detailFields.push({
              name: 'includesSpecificActivities',
              label: 'Application of pesticides or herbicides',
              value: 'Excluded',
              size: 6
            });

            return (
              <CollapsibleListItem
                key={`custom-farming-${index}`}
                title={`Custom farming ${String(index + 1).padStart(2, '0')}`}
                rightSideValues={[]}
                detailFields={detailFields}
                onDelete={() => {
                  const newItems = items.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newItems);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
                keyPrefix={`custom-farming-${index}`}
              />
            );
          })}

          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                description: '',
                amount: '',
                includesSpecificActivities: false
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const newItem = {
                  type: 'customFarming',
                  description: val.description,
                  amount: Number(val.amount),
                  includesSpecificActivities: false
                };
                setFieldValue(FIELD_NAME, [...items, newItem]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm }) => (
                <Grid container spacing={2}>
                  <Field
                    xs={12}
                    type="textarea"
                    id="description"
                    name="description"
                    mode="dark"
                    label="Description (required)"
                    disabled={disabled}
                  />
                  <Field
                    xs={6}
                    type="numeric"
                    id="amount"
                    name="amount"
                    mode="dark"
                    label="Estimated receipts (per year)"
                    disabled={disabled}
                    fieldProps={{ prefix: '$', thousandSeparator: true, decimalScale: 2 }}
                  />
                  <Field
                    xs={6}
                    type="value"
                    id="includesSpecificActivities"
                    name="includesSpecificActivities"
                    mode="dark"
                    label="Application of pesticides or herbicides"
                    formatter={() => 'Excluded'}
                  />
                  <Grid container alignItems="center" className={classes.addButtonRow}>
                    <Button
                      type="button"
                      mode="big"
                      variant="contained"
                      color="secondary"
                      className={classes.addButton}
                      disabled={disabled}
                      onClick={submitForm}
                    >
                      Add Item
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

CustomFarming.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default CustomFarming;
